import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, startOfWeek, addDays, subDays } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Users,
  UserPlus,
  DollarSign,
  Mail,
  Phone,
  TrendingUp,
  Percent,
  BarChart3,
  MapPin,
  Grid3X3,
  FileText,
  CalendarDays,
  Gauge,
  AlertTriangle,
} from "lucide-react";
import { Link } from "wouter";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FitnessRingCard } from "@/components/dashboard/fitness-ring";
import { FollowupsCard } from "@/components/dashboard/followups-card";
import { QuickActionCard } from "@/components/dashboard/quick-action-card";
import { GamificationWidget, EndOfDaySummaryBanner } from "@/components/gamification/widget";
import { ManagerGamificationTile } from "@/components/gamification/manager-tile";
import { TimecardControl } from "@/components/timecard/timecard-control";
import type { UserRole } from "@shared/schema";

type RangeType = "day" | "week" | "month" | "year";

interface SalesRepDashboardProps {
  effectiveRole?: UserRole;
  effectiveUserId?: string;
}

interface SummaryData {
  period: { start: string; end: string };
  metrics: {
    leadsCreated: { value: number; delta: number };
    clientsCreated: { value: number; delta: number };
    dollarsBooked: { value: number; delta: number };
    emailsSent: { value: number; delta: number };
    callsLogged: { value: number; delta: number };
  };
  quality: {
    avgBookingValue: { value: number; delta: number };
    closeRate: { value: number; delta: number };
  };
}

interface SeriesPoint {
  t: string;
  bookedDollars: number;
  bookingCount: number;
}

interface WeekData {
  weekStart: string;
  bookedDollars: number;
  bookingCount: number;
}

interface RegionData {
  regionId: string;
  regionName: string;
  bookedDollars: number;
  bookingCount: number;
  avgBookingValue: number;
}

interface Region {
  id: string;
  name: string;
}

interface SlippingLead {
  id: string;
  recordId: string | null;
  leadName: string | null;
  status: string;
  lastActivityAt: string | null;
  lastContacted: string | null;
  daysSinceTouch: number;
  rep: string | null;
}

interface SlippingResponse {
  staleDays: number;
  leads: SlippingLead[];
}

const RING_COLORS = {
  leads: "#22c55e",
  clients: "#3b82f6",
  dollars: "#f59e0b",
  emails: "#8b5cf6",
  calls: "#ec4899",
};

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatCurrencyFull(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function SalesRepDashboard({ effectiveRole, effectiveUserId }: SalesRepDashboardProps) {
  const [range, setRange] = useState<RangeType>("week");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [regionId, setRegionId] = useState("all");
  const [weekChartMode, setWeekChartMode] = useState<"dollars" | "bookings">("dollars");

  const dateParam = format(selectedDate, "yyyy-MM-dd");
  const userIdParam = effectiveUserId ? `&userId=${effectiveUserId}` : "";
  // Get the user's timezone offset in minutes (negative for timezones ahead of UTC)
  const tzOffset = new Date().getTimezoneOffset();
  const tzParam = `&tzOffset=${tzOffset}`;

  const { data: summary, isLoading: summaryLoading } = useQuery<SummaryData>({
    queryKey: ["/api/rep-dashboard/summary", range, dateParam, regionId, effectiveUserId, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/rep-dashboard/summary?range=${range}&date=${dateParam}&regionId=${regionId}${userIdParam}${tzParam}`
      );
      if (!res.ok) throw new Error("Failed to fetch summary");
      return res.json();
    },
  });

  const { data: bookedSeries, isLoading: seriesLoading } = useQuery<SeriesPoint[]>({
    queryKey: ["/api/rep-dashboard/booked-series", range, dateParam, regionId, effectiveUserId, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/rep-dashboard/booked-series?range=${range}&date=${dateParam}&regionId=${regionId}${userIdParam}${tzParam}`
      );
      if (!res.ok) throw new Error("Failed to fetch series");
      return res.json();
    },
  });

  const { data: weekToWeek, isLoading: weekLoading } = useQuery<WeekData[]>({
    queryKey: ["/api/rep-dashboard/week-to-week", dateParam, regionId, effectiveUserId, tzOffset],
    queryFn: async () => {
      const res = await fetch(
        `/api/rep-dashboard/week-to-week?weeks=12&date=${dateParam}&regionId=${regionId}${userIdParam}${tzParam}`
      );
      if (!res.ok) throw new Error("Failed to fetch week data");
      return res.json();
    },
  });

  const { data: topRegions, isLoading: regionsLoading } = useQuery<RegionData[]>({
    queryKey: ["/api/rep-dashboard/top-regions", range, dateParam, effectiveUserId, tzOffset],
    queryFn: async () => {
      const res = await fetch(`/api/rep-dashboard/top-regions?range=${range}&date=${dateParam}${userIdParam}${tzParam}`);
      if (!res.ok) throw new Error("Failed to fetch regions");
      return res.json();
    },
  });

  const { data: myRegions } = useQuery<Region[]>({
    queryKey: ["/api/rep-dashboard/my-regions", effectiveUserId],
    queryFn: async () => {
      const res = await fetch(`/api/rep-dashboard/my-regions?${userIdParam.replace("&", "")}`);
      if (!res.ok) throw new Error("Failed to fetch regions");
      return res.json();
    },
  });

  // Configurable per-rep goals — pulled from /api/goals/effective which
  // honors workspace org defaults + per-user overrides. Falls back to
  // sensible legacy constants if the workspace hasn't configured any.
  type EffectiveGoal = { metricKey: string; period: string; target: number };
  const { data: effectiveGoals } = useQuery<{ goals: EffectiveGoal[] }>({
    queryKey: ["/api/goals/effective", effectiveUserId],
    queryFn: async () => {
      const url = effectiveUserId ? `/api/goals/effective?userId=${effectiveUserId}` : "/api/goals/effective";
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch goals");
      return res.json();
    },
  });

  const { data: slipping, isLoading: slippingLoading } = useQuery<SlippingResponse>({
    queryKey: ["/api/rep-dashboard/slipping", effectiveUserId, 7],
    queryFn: async () => {
      const url = `/api/rep-dashboard/slipping?staleDays=7&limit=10${
        effectiveUserId ? `&userId=${effectiveUserId}` : ""
      }`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch slipping leads");
      return res.json();
    },
  });

  // Map the configured goals into the period the rep is currently viewing.
  // Treat "day" + "week" + "month" + "year" as their literal periods; fall
  // through to monthly if the workspace only configured a monthly goal.
  const goalFor = (metricKey: string, fallback: number): number => {
    if (!effectiveGoals?.goals?.length) return fallback;
    const period = range === "day" ? "daily" : range === "week" ? "weekly" : range === "month" ? "monthly" : "yearly";
    const exact = effectiveGoals.goals.find(g => g.metricKey === metricKey && g.period === period);
    if (exact) return exact.target;
    // Sensible derivation: scale the monthly goal if we don't have an exact match.
    const monthly = effectiveGoals.goals.find(g => g.metricKey === metricKey && g.period === "monthly");
    if (!monthly) return fallback;
    if (period === "daily") return Math.max(1, Math.round(monthly.target / 22));
    if (period === "weekly") return Math.max(1, Math.round(monthly.target / 4));
    if (period === "yearly") return monthly.target * 12;
    return monthly.target;
  };

  const navigateDate = (direction: "prev" | "next") => {
    const newDate = new Date(selectedDate);
    const offset = direction === "prev" ? -1 : 1;

    switch (range) {
      case "day":
        newDate.setDate(newDate.getDate() + offset);
        break;
      case "week":
        newDate.setDate(newDate.getDate() + offset * 7);
        break;
      case "month":
        newDate.setMonth(newDate.getMonth() + offset);
        break;
      case "year":
        newDate.setFullYear(newDate.getFullYear() + offset);
        break;
    }
    setSelectedDate(newDate);
  };

  const dateLabel = useMemo(() => {
    switch (range) {
      case "day":
        return format(selectedDate, "EEEE, MMMM d, yyyy");
      case "week": {
        const weekStart = startOfWeek(selectedDate);
        const weekEnd = addDays(weekStart, 6);
        return `${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d, yyyy")}`;
      }
      case "month":
        return format(selectedDate, "MMMM yyyy");
      case "year":
        return format(selectedDate, "yyyy");
    }
  }, [selectedDate, range]);

  const ringMetrics = useMemo(() => {
    if (!summary) return [];
    const m = summary.metrics;
    return [
      {
        key: "leads",
        value: m.leadsCreated.value,
        delta: m.leadsCreated.delta,
        label: "Leads Created",
        color: RING_COLORS.leads,
        goal: goalFor("leads", 20),
        icon: <Users className="w-4 h-4" />,
      },
      {
        key: "clients",
        value: m.clientsCreated.value,
        delta: m.clientsCreated.delta,
        label: "Clients Created",
        color: RING_COLORS.clients,
        goal: goalFor("clients", 5),
        icon: <UserPlus className="w-4 h-4" />,
      },
      {
        key: "dollars",
        value: m.dollarsBooked.value,
        delta: m.dollarsBooked.delta,
        label: "Dollars Booked",
        color: RING_COLORS.dollars,
        goal: goalFor("dollars_booked", 5000),
        icon: <DollarSign className="w-4 h-4" />,
        format: formatCurrency,
      },
      {
        key: "emails",
        value: m.emailsSent.value,
        delta: m.emailsSent.delta,
        label: "Emails Sent",
        color: RING_COLORS.emails,
        goal: goalFor("emails", 50),
        icon: <Mail className="w-4 h-4" />,
      },
      {
        key: "calls",
        value: m.callsLogged.value,
        delta: m.callsLogged.delta,
        label: "Calls Logged",
        color: RING_COLORS.calls,
        goal: goalFor("calls", 30),
        icon: <Phone className="w-4 h-4" />,
      },
    ];
  }, [summary, effectiveGoals, range]);

  // Personal pacing: how much of the period has elapsed vs how much of
  // goal you've achieved. Only meaningful for week/month/year — day is
  // treated as 100% elapsed at end of day. "Pace status" = ahead if
  // achievement% > elapsed%, on if within ±10pts, behind otherwise.
  const pacing = useMemo(() => {
    if (!summary) return null;
    const now = new Date();
    let elapsedPct = 100;
    if (range === "week") {
      const start = startOfWeek(selectedDate);
      const end = addDays(start, 7);
      const total = end.getTime() - start.getTime();
      const so_far = Math.min(total, Math.max(0, now.getTime() - start.getTime()));
      elapsedPct = (so_far / total) * 100;
    } else if (range === "month") {
      const start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
      const total = end.getTime() - start.getTime();
      const so_far = Math.min(total, Math.max(0, now.getTime() - start.getTime()));
      elapsedPct = (so_far / total) * 100;
    } else if (range === "year") {
      const start = new Date(selectedDate.getFullYear(), 0, 1);
      const end = new Date(selectedDate.getFullYear() + 1, 0, 1);
      const total = end.getTime() - start.getTime();
      const so_far = Math.min(total, Math.max(0, now.getTime() - start.getTime()));
      elapsedPct = (so_far / total) * 100;
    }
    // Cap at 100% so end-of-period doesn't show 102%.
    elapsedPct = Math.max(0, Math.min(100, elapsedPct));

    const rows = [
      {
        key: "dollars_booked",
        label: "Booked $",
        value: summary.metrics.dollarsBooked.value,
        goal: goalFor("dollars_booked", 5000),
        fmt: (n: number) => formatCurrency(n),
      },
      {
        key: "leads",
        label: "Leads",
        value: summary.metrics.leadsCreated.value,
        goal: goalFor("leads", 20),
        fmt: (n: number) => String(Math.round(n)),
      },
      {
        key: "calls",
        label: "Calls",
        value: summary.metrics.callsLogged.value,
        goal: goalFor("calls", 30),
        fmt: (n: number) => String(Math.round(n)),
      },
      {
        key: "emails",
        label: "Emails",
        value: summary.metrics.emailsSent.value,
        goal: goalFor("emails", 50),
        fmt: (n: number) => String(Math.round(n)),
      },
    ].map((r) => {
      const achievementPct = r.goal > 0 ? (r.value / r.goal) * 100 : 0;
      const delta = achievementPct - elapsedPct;
      const status: "ahead" | "on" | "behind" =
        delta > 10 ? "ahead" : delta < -10 ? "behind" : "on";
      return { ...r, achievementPct, delta, status };
    });
    return { elapsedPct, rows };
  }, [summary, effectiveGoals, range, selectedDate]);

  const maxRegionDollars = useMemo(() => {
    if (!topRegions || topRegions.length === 0) return 1;
    return Math.max(...topRegions.map((r) => r.bookedDollars));
  }, [topRegions]);

  return (
    <div className="flex flex-col h-full overflow-auto bg-background">
      <div className="sticky top-0 z-10 bg-background border-b">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-6">
          <div>
            <h1 className="text-2xl font-bold">Sales Dashboard</h1>
            <p className="text-sm text-muted-foreground">{dateLabel}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center border rounded-lg overflow-hidden">
              {(["day", "week", "month", "year"] as RangeType[]).map((r) => (
                <Button
                  key={r}
                  variant={range === r ? "default" : "ghost"}
                  size="sm"
                  className="rounded-none"
                  onClick={() => setRange(r)}
                  data-testid={`range-${r}`}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateDate("prev")}
                data-testid="button-prev-date"
                aria-label={`Previous ${range}`}
              >
                <ChevronLeft className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(new Date())}
                data-testid="button-today"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateDate("next")}
                data-testid="button-next-date"
                aria-label={`Next ${range}`}
              >
                <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </Button>
            </div>
            {myRegions && myRegions.length > 0 && (
              <Select value={regionId} onValueChange={setRegionId}>
                <SelectTrigger className="w-40" data-testid="select-region">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All My Regions</SelectItem>
                  {myRegions.map((region) => (
                    <SelectItem key={region.id} value={region.id}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <TimecardControl />
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 sm:p-6 space-y-6">
        <EndOfDaySummaryBanner />
        <GamificationWidget targetUserId={effectiveUserId} />
        <ManagerGamificationTile />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <FitnessRingCard metrics={ringMetrics} isLoading={summaryLoading} />
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Booked Revenue
                  </CardTitle>
                  <Badge variant="secondary">
                    {summary ? formatCurrencyFull(summary.metrics.dollarsBooked.value) : "-"}
                  </Badge>
                </CardHeader>
                <CardContent>
                  {seriesLoading ? (
                    <Skeleton className="h-64 w-full" />
                  ) : bookedSeries && bookedSeries.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={bookedSeries}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="t" className="text-xs" tick={{ fill: "var(--muted-foreground)" }} />
                        <YAxis
                          tickFormatter={(v) => formatCurrency(v)}
                          className="text-xs"
                          tick={{ fill: "var(--muted-foreground)" }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "var(--popover)",
                            border: "1px solid var(--border)",
                            borderRadius: "8px",
                          }}
                          formatter={(value: number) => [formatCurrencyFull(value), "Revenue"]}
                        />
                        <Area
                          type="monotone"
                          dataKey="bookedDollars"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          fill="url(#colorRevenue)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No bookings in this period</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.div
            className="h-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <FollowupsCard className="h-full" />
          </motion.div>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Week over Week
                </CardTitle>
                <div
                  className="flex gap-1"
                  role="group"
                  aria-label="Week-over-week chart mode"
                >
                  <Button
                    size="sm"
                    variant={weekChartMode === "dollars" ? "default" : "ghost"}
                    onClick={() => setWeekChartMode("dollars")}
                    data-testid="button-week-dollars"
                    aria-label="Show revenue"
                    aria-pressed={weekChartMode === "dollars"}
                  >
                    $
                  </Button>
                  <Button
                    size="sm"
                    variant={weekChartMode === "bookings" ? "default" : "ghost"}
                    onClick={() => setWeekChartMode("bookings")}
                    data-testid="button-week-bookings"
                    aria-label="Show booking count"
                    aria-pressed={weekChartMode === "bookings"}
                  >
                    #
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {weekLoading ? (
                  <Skeleton className="h-64 w-full" />
                ) : weekToWeek && weekToWeek.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={weekToWeek}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="weekStart"
                        tickFormatter={(v) => format(new Date(v), "M/d")}
                        className="text-xs"
                        tick={{ fill: "var(--muted-foreground)" }}
                      />
                      <YAxis
                        tickFormatter={(v) =>
                          weekChartMode === "dollars" ? formatCurrency(v) : v.toString()
                        }
                        className="text-xs"
                        tick={{ fill: "var(--muted-foreground)" }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "var(--popover)",
                          border: "1px solid var(--border)",
                          borderRadius: "8px",
                        }}
                        formatter={(value: number) => [
                          weekChartMode === "dollars" ? formatCurrencyFull(value) : value,
                          weekChartMode === "dollars" ? "Revenue" : "Bookings",
                        ]}
                        labelFormatter={(label) => format(new Date(label), "MMM d, yyyy")}
                      />
                      <Bar
                        dataKey={weekChartMode === "dollars" ? "bookedDollars" : "bookingCount"}
                        fill="#3b82f6"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <p>No data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Top Regions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {regionsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : topRegions && topRegions.length > 0 ? (
                  <div className="space-y-3">
                    {topRegions.slice(0, 5).map((region, idx) => (
                      <div key={region.regionId} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium truncate">{region.regionName}</span>
                          <span className="text-muted-foreground">
                            {formatCurrency(region.bookedDollars)} ({region.bookingCount})
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{
                              backgroundColor: `hsl(${220 - idx * 30}, 70%, 50%)`,
                            }}
                            initial={{ width: 0 }}
                            animate={{
                              width: `${(region.bookedDollars / maxRegionDollars) * 100}%`,
                            }}
                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No regional data yet</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.45 }}
          >
            <Card data-testid="card-personal-pacing">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <Gauge className="w-5 h-5" />
                    Personal Pacing
                  </span>
                  {pacing && (
                    <Badge variant="secondary" data-testid="badge-period-elapsed">
                      {Math.round(pacing.elapsedPct)}% of {range} elapsed
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!pacing ? (
                  <Skeleton className="h-32 w-full" />
                ) : (
                  <div className="space-y-3">
                    {pacing.rows.map((r) => {
                      const barPct = Math.max(0, Math.min(100, r.achievementPct));
                      const barColor =
                        r.status === "ahead"
                          ? "bg-green-500"
                          : r.status === "behind"
                            ? "bg-red-500"
                            : "bg-blue-500";
                      const statusLabel =
                        r.status === "ahead"
                          ? "Ahead"
                          : r.status === "behind"
                            ? "Behind"
                            : "On pace";
                      const statusClass =
                        r.status === "ahead"
                          ? "text-green-500"
                          : r.status === "behind"
                            ? "text-red-500"
                            : "text-muted-foreground";
                      return (
                        <div key={r.key} className="space-y-1" data-testid={`row-pacing-${r.key}`}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{r.label}</span>
                            <span className="text-muted-foreground">
                              {r.fmt(r.value)} / {r.fmt(r.goal)}
                              <span className={`ml-2 font-medium ${statusClass}`}>{statusLabel}</span>
                            </span>
                          </div>
                          <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                            <motion.div
                              className={`h-full rounded-full ${barColor}`}
                              initial={{ width: 0 }}
                              animate={{ width: `${barPct}%` }}
                              transition={{ duration: 0.5 }}
                            />
                            {/* Period elapsed pace marker */}
                            <div
                              className="absolute top-0 bottom-0 w-px bg-foreground/70"
                              style={{ left: `${pacing.elapsedPct}%` }}
                              aria-label={`Pace marker at ${Math.round(pacing.elapsedPct)}%`}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-xs text-muted-foreground pt-2">
                      Vertical line = where you should be by now. Bar past the line = ahead of goal.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <Card data-testid="card-slipping-leads">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    What's Slipping
                  </span>
                  {slipping && (
                    <Badge variant="secondary" data-testid="badge-slipping-count">
                      {slipping.leads.length} leads, {slipping.staleDays}+ days
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {slippingLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : slipping && slipping.leads.length > 0 ? (
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {slipping.leads.map((lead) => (
                      <Link
                        key={lead.id}
                        href={`/leads?focus=${lead.id}`}
                        data-testid={`link-slipping-lead-${lead.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 py-2 px-2 rounded hover-elevate cursor-pointer">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">
                              {lead.leadName || lead.recordId || "Untitled lead"}
                            </div>
                            <div className="text-xs text-muted-foreground capitalize">
                              {lead.status}
                            </div>
                          </div>
                          <Badge
                            variant={lead.daysSinceTouch >= 30 ? "destructive" : "secondary"}
                            data-testid={`badge-slipping-days-${lead.id}`}
                          >
                            {lead.daysSinceTouch >= 9999 ? "Never" : `${lead.daysSinceTouch}d`}
                          </Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                    Nothing slipping — all open leads have been touched recently.
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-green-500/10">
                  <DollarSign className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg Booking Value</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-bold">
                      {summary && summary.quality.avgBookingValue.value > 0
                        ? formatCurrencyFull(summary.quality.avgBookingValue.value)
                        : "—"}
                    </p>
                    {summary && summary.quality.avgBookingValue.value > 0 && (
                      <span
                        className={`text-sm ${
                          summary.quality.avgBookingValue.delta >= 0
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        <span className="sr-only">
                          {summary.quality.avgBookingValue.delta >= 0 ? "Up " : "Down "}
                        </span>
                        <span aria-hidden="true">
                          {summary.quality.avgBookingValue.delta >= 0 ? "↑" : "↓"}
                        </span>
                        {Math.abs(summary.quality.avgBookingValue.delta)}%
                        <span className="sr-only"> vs prior period</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-blue-500/10">
                  <Percent className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Close Rate</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-bold">
                      {summary && summary.quality.closeRate.value > 0
                        ? `${summary.quality.closeRate.value.toFixed(1)}%`
                        : "—"}
                    </p>
                    {summary && summary.quality.closeRate.value > 0 && (
                      <span
                        className={`text-sm ${
                          summary.quality.closeRate.delta >= 0 ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        <span className="sr-only">
                          {summary.quality.closeRate.delta >= 0 ? "Up " : "Down "}
                        </span>
                        <span aria-hidden="true">
                          {summary.quality.closeRate.delta >= 0 ? "↑" : "↓"}
                        </span>
                        {Math.abs(summary.quality.closeRate.delta)}%
                        <span className="sr-only"> vs prior period</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
